/**
 * recommendationEngine.js — turns eligibility + fit into an explainable
 * recommendation with an honest state, never forcing a false single answer.
 *
 * States:
 *   collecting_data      — material discovery questions remain
 *   multiple_candidates  — eligibility settled; intent distinctions remain
 *   pricing_required     — a pricing-contingent product (ARM/IO) could win,
 *                          but live pricing must prove the benefit first
 *   single_best_fit      — one product, fully explainable
 *   no_supported_product — nothing in the catalog matches; say so honestly
 */
import { evaluateEligibility } from './eligibilityEngine.js';
import { scoreFit } from './fitScoringEngine.js';
import { selectNextQuestion, buildContext } from './nextQuestionEngine.js';
import { forwardMortgageUniverse, adjacentPathwayProducts, CATALOG_VERSION } from './productCatalog.js';
import { nullPricingProvider } from './pricingProvider.js';
import { questionByKey } from './questionRegistry.js';

const FAMILY_PREFERENCE = { conventional: 0, va: 1, fha: 2, usda: 3, nonqm: 4 };

/** Within a family, plain standard execution beats niche variants on ties. */
const SUBFAMILY_PREFERENCE = [
  'Agency Conforming', 'Standard', 'Purchase', 'Refinance', 'High-Balance Agency',
];
const subFamilyRank = (p) => {
  const i = SUBFAMILY_PREFERENCE.indexOf(p.subFamily);
  return i === -1 ? SUBFAMILY_PREFERENCE.length : i;
};

/** Documentation burden: non-credit-qualifying streamline < streamline < full doc < alt-doc/manual. */
function docBurden(prod) {
  if (/no income\/dti qualification/i.test(String(prod.qualification.dtiMethod || ''))) return -0.5;
  if (prod.transaction.streamline) return 0;
  if (prod.qualification.altDoc || prod.underwriting.manualUw === 'yes') return 2;
  return 1;
}

/**
 * Deterministic tie-breakers, in the design brief's order. Returns the
 * preferred evaluation of the two.
 */
function breakTie(a, b, profile) {
  const rules = [
    (x, y) => y.confidence - x.confidence, // 1. eligibility confidence
    (x, y) => { // 2. lower borrower/overlay risk
      const rank = { low: 0, medium: 1, high: 2, 'very-high': 3, 'program-specific': 2 };
      return rank[x.product.governance.overlayRisk] - rank[y.product.governance.overlayRisk];
    },
    (x, y) => y.fitScore - x.fitScore, // 3. objective fit
    (x, y) => { // 4/5. payment comfort & stability via cautions count
      return x.cautions.length - y.cautions.length;
    },
    (x, y) => docBurden(x.product) - docBurden(y.product), // 6. doc burden
    (x, y) => { // 7. lifetime cost when payoff/cost is the goal
      if (profile.primaryMortgageGoal === 'fast-payoff' || profile.primaryMortgageGoal === 'lowest-interest') {
        return (y.product.routing.fit.interestEfficiency.score ?? 0) - (x.product.routing.fit.interestEfficiency.score ?? 0);
      }
      return 0;
    },
    (x, y) => FAMILY_PREFERENCE[x.product.family] - FAMILY_PREFERENCE[y.product.family], // 10. standard first
    (x, y) => subFamilyRank(x.product) - subFamilyRank(y.product), // plain execution over niche variant
    (x, y) => (x.product.termProgramId < y.product.termProgramId ? -1 : 1), // absolute determinism
  ];
  for (const rule of rules) {
    const d = rule(a, b);
    if (d < 0) return a;
    if (d > 0) return b;
  }
  return a;
}

function sortCandidates(list, profile) {
  return [...list].sort((a, b) => {
    if (Math.abs(a.fitScore - b.fitScore) > 2) return b.fitScore - a.fitScore;
    return breakTie(a, b, profile) === a ? -1 : 1;
  });
}

/**
 * ARM/IO pricing gate: a pricing-contingent product may only beat the best
 * pricing-independent product when live pricing proves a meaningful benefit
 * AND the borrower accepts reset risk. Without pricing it can tie at
 * "pricing_required", never win outright.
 */
function pricingGate(best, contender, profile, pricingProvider) {
  if (!contender) return { winner: best, state: null };
  if (!best) return { winner: contender, state: 'pricing_required' };
  if (contender.fitScore < best.fitScore - 5) return { winner: best, state: null };

  if (pricingProvider && pricingProvider.available) {
    const cp = pricingProvider.getPricing(contender.product.termProgramId);
    const bp = pricingProvider.getPricing(best.product.termProgramId);
    if (cp && bp && cp.principalAndInterest != null && bp.principalAndInterest != null) {
      const savings = bp.principalAndInterest - cp.principalAndInterest;
      const meaningful = savings > 0 && savings / bp.principalAndInterest >= 0.03;
      const riskOk = profile.rateRiskTolerance === 'yes' || profile.rateRiskTolerance === 'maybe';
      if (meaningful && riskOk && contender.fitScore >= best.fitScore - 5) {
        return { winner: contender, state: null };
      }
      return { winner: best, state: null }; // priced, no meaningful benefit → fixed stands
    }
  }
  return { winner: best, state: 'pricing_required' };
}

/**
 * Full evaluation → recommendation.
 *
 * @param {import('./borrowerProfile').BorrowerProfile} profile
 * @param {object} [opts]
 * @param {Set<string>} [opts.answeredKeys] question keys already answered/asked
 * @param {object} [opts.pricingProvider]
 * @param {Array} [opts.universe] override product universe (tests)
 */
export function recommendProducts(profile, opts = {}) {
  const pricingProvider = opts.pricingProvider || nullPricingProvider;
  const universe = opts.universe || forwardMortgageUniverse();
  const answeredKeys = opts.answeredKeys || new Set();

  const evaluations = evaluateEligibility(profile, universe).map((e) => {
    if (e.status === 'ineligible') return { ...e, fitScore: 0, confidence: 0, fitReasons: [], fitCautions: [] };
    const fit = scoreFit(profile, e, pricingProvider);
    return { ...e, ...fit, cautions: fit.fitCautions };
  });

  const eligible = evaluations.filter((e) => e.status === 'eligible');
  const needsData = evaluations.filter((e) => e.status === 'needs_data');
  const progress = {
    total: universe.length,
    eliminated: evaluations.length - eligible.length - needsData.length,
    eligible: eligible.length,
    needsData: needsData.length,
    remaining: eligible.length + needsData.length,
  };

  const nextQ = selectNextQuestion(profile, evaluations, answeredKeys);
  const ranked = sortCandidates(eligible, profile);

  const base = {
    catalogVersion: CATALOG_VERSION,
    evaluations,
    candidates: ranked.slice(0, 8),
    progress,
    nextQuestion: nextQ,
    adjacentPathways: adjacentPathways(profile),
  };

  // Nothing can ever match
  if (!eligible.length && !needsData.length) {
    return { ...base, state: 'no_supported_product', best: null };
  }

  // Material discovery still ahead?
  if (nextQ) {
    const q = questionByKey(nextQ.key);
    const settled = eligible.length > 0 && (q.stage === 'intent' || q.stage === 'conditional');
    return { ...base, state: settled ? 'multiple_candidates' : 'collecting_data', best: ranked[0] || null };
  }

  if (!eligible.length) {
    // Only unresolvable needs_data remain (inputs with no registry question)
    return { ...base, state: 'collecting_data', best: null };
  }

  // Pricing gate: best pricing-independent vs best pricing-contingent
  const bestFirm = ranked.find((e) => !e.pricingRequired) || null;
  const bestContingent = ranked.find((e) => e.pricingRequired) || null;
  const gate = pricingGate(bestFirm, bestContingent, profile, pricingProvider);
  const winner = gate.winner;

  if (!winner) return { ...base, state: 'no_supported_product', best: null };

  // A needs_data product could still overtake the winner if its inputs were
  // resolvable — but nextQ === null means no registry question can resolve
  // them, so they are annotated rather than blocking.
  const state = gate.state === 'pricing_required' ? 'pricing_required' : 'single_best_fit';
  return {
    ...base,
    state,
    best: winner,
    compareWithPricing: state === 'pricing_required' && bestContingent ? [bestContingent] : [],
  };
}

/** Non-ranked adjacent pathways (kept out of forward-mortgage scoring). */
function adjacentPathways(profile) {
  const out = [];
  if (profile.borrowerAge !== undefined && profile.borrowerAge >= 62 && profile.occupancy === 'primary') {
    const hecms = adjacentPathwayProducts().filter((p) => p.productClass === 'reverse');
    if (hecms.length) {
      out.push({
        kind: 'reverse-mortgage',
        note: 'Age 62+ homeowners may also want to explore a HECM reverse mortgage — evaluated separately from standard mortgages.',
        products: hecms.map((p) => p.termProgramId),
      });
    }
  }
  return out;
}

export { buildContext };
